import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export const MODES = ["full", "reduced", "off"];
// Decorative animations disabled in "reduced" (numbers from mirra-motion.html)
export const DECORATIVE = [13, 14, 15, 16, 19, 20, 26, 27, 29];
const KEY = "pd_motion";

const systemDefault = () => (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "reduced" : "full");
const stored = () => (MODES.includes(localStorage.getItem(KEY)) ? localStorage.getItem(KEY) : null);

const MotionCtx = createContext({ mode: "full", setMode: () => {}, allows: () => true });

export function MotionProvider({ children }) {
  const { user, setUser } = useAuth();
  const [mode, setModeState] = useState(() => stored() || systemDefault());

  useEffect(() => {
    if (user?.animations && MODES.includes(user.animations)) {
      setModeState(user.animations);
      localStorage.setItem(KEY, user.animations);
    }
  }, [user?.animations]);

  useEffect(() => { document.documentElement.dataset.motion = mode; }, [mode]);

  const setMode = useCallback(async (m) => {
    if (!MODES.includes(m)) return;
    setModeState(m);
    localStorage.setItem(KEY, m);
    if (user) {
      try { const { data } = await api.put("/auth/me/preferences", { animations: m }); setUser(data); } catch (e) { /* kept locally */ }
    }
  }, [user, setUser]);

  const allows = useCallback((n) => mode === "full" || (mode === "reduced" && !DECORATIVE.includes(n)), [mode]);
  const value = useMemo(() => ({ mode, setMode, allows }), [mode, setMode, allows]);
  return <MotionCtx.Provider value={value}>{children}</MotionCtx.Provider>;
}

export const useMotion = () => useContext(MotionCtx);
